// Event Locations API client. Gracefully degrades when backend is not configured.

const BASE_URL =
  process.env.REACT_APP_EVENT_LOCATIONS_API_URL ||
  "https://fixlay1z59.execute-api.us-east-1.amazonaws.com/prod";

let warnedNotConfigured = false;
const warnNotConfiguredOnce = () => {
  if (!warnedNotConfigured) {
    // eslint-disable-next-line no-console
    console.info(
      "Event Locations API not configured; falling back to legacy hardcoded list."
    );
    warnedNotConfigured = true;
  }
};

const isConfigured = () => !!BASE_URL && !BASE_URL.includes("PLACEHOLDER");

const getToken = () => sessionStorage.getItem("cognitoIdToken");

const buildHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

const baseUrl = () => BASE_URL.replace(/\/$/, "");

export const searchLocations = async (q) => {
  const token = getToken();
  if (!isConfigured() || !token) {
    warnNotConfiguredOnce();
    return { results: [], truncated: false };
  }

  const url = new URL(`${baseUrl()}/event-locations`);
  if (q && String(q).trim().length > 0) {
    url.searchParams.set("q", String(q));
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to search locations: ${response.status} ${response.statusText}`
    );
  }

  const data = await response.json();
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    truncated: !!data?.truncated,
  };
};

export const listLocations = async (cursor) => {
  const token = getToken();
  if (!isConfigured() || !token) {
    warnNotConfiguredOnce();
    return { items: [], cursor: null };
  }

  const url = new URL(`${baseUrl()}/event-locations`);
  if (cursor) {
    url.searchParams.set("cursor", cursor);
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to list locations: ${response.status} ${response.statusText}`
    );
  }

  const data = await response.json();
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    cursor: data?.cursor || null,
  };
};

export const addLocation = async ({ title, address }) => {
  const token = getToken();
  if (!isConfigured() || !token) {
    warnNotConfiguredOnce();
    return { item: null };
  }

  const response = await fetch(`${baseUrl()}/event-locations`, {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify({ title, address }),
  });

  if (!response.ok) {
    let message = `Failed to add location: ${response.status} ${response.statusText}`;
    try {
      const errBody = await response.json();
      if (errBody) message = errBody.error || errBody.message || message;
    } catch (e) {
      // ignore
    }
    throw new Error(message);
  }

  const data = await response.json();
  return { item: data?.item || null };
};

export const patchLocation = async (locationId, body) => {
  const token = getToken();
  if (!isConfigured() || !token) {
    warnNotConfiguredOnce();
    return null;
  }

  const response = await fetch(
    `${baseUrl()}/event-locations/${encodeURIComponent(locationId)}`,
    {
      method: "PATCH",
      headers: buildHeaders(token),
      body: JSON.stringify(body || {}),
    }
  );

  if (!response.ok) {
    let message = `Failed to update location: ${response.status} ${response.statusText}`;
    try {
      const errBody = await response.json();
      if (errBody) message = errBody.error || errBody.message || message;
    } catch (e) {
      // ignore
    }
    throw new Error(message);
  }

  const data = await response.json();
  return (data && data.item) || null;
};

export const deleteLocation = async (locationId) => {
  const token = getToken();
  if (!isConfigured() || !token) {
    warnNotConfiguredOnce();
    return;
  }

  const response = await fetch(
    `${baseUrl()}/event-locations/${encodeURIComponent(locationId)}`,
    {
      method: "DELETE",
      headers: buildHeaders(token),
    }
  );

  if (!response.ok && response.status !== 204) {
    let message = `Failed to delete location: ${response.status} ${response.statusText}`;
    try {
      const errBody = await response.json();
      if (errBody) message = errBody.error || errBody.message || message;
    } catch (e) {
      // ignore
    }
    throw new Error(message);
  }
};

export const runBackfill = async () => {
  const token = getToken();
  if (!isConfigured() || !token) {
    warnNotConfiguredOnce();
    return {
      scanned: 0,
      normalized: 0,
      upserted: 0,
      skippedJunk: 0,
      durationMs: 0,
    };
  }

  const response = await fetch(`${baseUrl()}/event-locations/backfill`, {
    method: "POST",
    headers: buildHeaders(token),
  });

  if (!response.ok) {
    let message = `Failed to run backfill: ${response.status} ${response.statusText}`;
    try {
      const errBody = await response.json();
      if (errBody) message = errBody.error || errBody.message || message;
    } catch (e) {
      // ignore
    }
    throw new Error(message);
  }

  const data = await response.json();
  return {
    scanned: Number(data?.scanned) || 0,
    normalized: Number(data?.normalized) || 0,
    upserted: Number(data?.upserted) || 0,
    skippedJunk: Number(data?.skippedJunk) || 0,
    durationMs: Number(data?.durationMs) || 0,
  };
};
